import { get } from 'lodash';
import filter from 'lodash/filter';
import { IEquipmentTypes, VehicleEquipmentCategoryEnum } from 'next-common';
import React, { useEffect, useState } from 'react';
import { useDisableSkus, useEnableSkus, useSkusPriceUpdate, useUnselectSkus } from '../../../../../common';
import { createSkuListFromValue, eventEmitter, Events, priceFormatter, VehicleConfig } from '../../../../../utils';
import { IModelPackages } from '../../VehiclePickerPage';
import { CarConfigureGroupStyled } from '../ResultGrid.styled';
import { VehicleSelectableDescription } from './VehicleSelectableDescription';
import { Card } from '@next-components/card';
import { CardType } from '@next-components/common-types';
import { MessagebarSeverityLevel } from '@next-components/messagebar';
export interface IVehicleSelectableEquipment {
  data: IModelPackages;
  customerType?: string;
  selectedEquipmentIds: IEquipmentTypes[];
  onSelectEquipmentChange: (
    data: IEquipmentTypes[],
    removeId?: string | null,
    isOptimisticUpdate?: boolean,
    configType?: VehicleConfig | VehicleEquipmentCategoryEnum,
    dealerPriceExclVat?: number,
  ) => void;
  disabled: boolean;
  equipmentCategory: VehicleEquipmentCategoryEnum;
  updatePrices: () => void;
}

export const VehicleSelectableEquipment = (props: IVehicleSelectableEquipment) => {
  const { data, selectedEquipmentIds, onSelectEquipmentChange, disabled, equipmentCategory } = props;
  const { isDisableSKUonChange, isDisableSKUonLoad, disableSku, onloadDisableSku } = useDisableSkus();
  const { isEnableSKUonChange } = useEnableSkus();
  const { getUnselectSkuFlag } = useUnselectSkus();
  const priceUpdate = useSkusPriceUpdate();
  const [isPrepared, setIsPrepared] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);
  const [activeDesc, setActiveDesc] = useState<IEquipmentTypes>(null);

  useEffect(() => {
    eventEmitter.subscribe(Events.SKU_RULES_PREPARED, setIsPrepared);
    return () => {
      eventEmitter.unsubscribe(Events.SKU_RULES_PREPARED);
    };
  }, []);

  const onSelectableInputChange = (id: string, equipment: IEquipmentTypes) => {
    let codes = [...selectedEquipmentIds];
    if (!id) {
      codes = codes?.filter((selected) => selected?.id !== equipment?.id);
    } else {
      codes.push(equipment);
    }
    onSelectEquipmentChange(codes, !id ? equipment?.id : null, false, VehicleConfig?.SELECTABLE_EQUIPMENT, equipment?.retailPrice);
  };

  const getPrice = (equipment: IEquipmentTypes) => {
    return priceUpdate?.has(equipment?.sku) ? Number(priceUpdate?.get(equipment?.sku)) : equipment?.priceExcludingVat;
  };

  useEffect(() => {
    //Update prices in cart & vat calculation
    if (selectedEquipmentIds?.length) {
      props.updatePrices();
    }
  }, [selectedEquipmentIds, priceUpdate]);

  const priceAdditionCheckToSelectable = (equipment: IEquipmentTypes) => priceFormatter(getPrice(equipment), equipment);

  const isDisabled = (sku: string) => {
    const isDisable = isDisableSKUonChange(sku) ? true : isEnableSKUonChange(sku) ? false : isDisableSKUonLoad(sku);
    return isDisable;
  };

  const isDisableRuleApply = (equipment: IEquipmentTypes) => {
    const isDisable = isDisabled(equipment?.sku);
    const isUnselectedSku = getUnselectSkuFlag(equipment?.sku);
    const isSelected = selectedEquipmentIds?.find((selected) => selected?.id === equipment?.id);
    if ((isDisable || isUnselectedSku) && isPrepared && isSelected) {
      let codes = [...selectedEquipmentIds];
      codes = codes?.filter((selected) => selected?.id !== equipment?.id);
      onSelectEquipmentChange(codes, equipment?.id, true, VehicleConfig?.SELECTABLE_EQUIPMENT, equipment?.retailPrice);
    }
    return isDisable;
  };

  useEffect(() => {
    selectedEquipmentIds?.forEach((selectedEquipment) => {
      const isDisable = isDisabled(selectedEquipment?.sku);
      if (!isDisable && isPrepared && selectedEquipment) {
        onSelectEquipmentChange(selectedEquipmentIds, null, true, VehicleConfig?.SELECTABLE_EQUIPMENT, selectedEquipment?.retailPrice);
      }
    });
  }, [selectedEquipmentIds]);

  const isDataAvailable = (equipment: IEquipmentTypes) => {
    return get(equipment, 'description');
  };

  const setCurrentData = (equipment: IEquipmentTypes) => {
    setActiveDesc(equipment);
    setActive(!active);
  };

  const getSkuNote = (sku: string) => {
    return createSkuListFromValue(disableSku, onloadDisableSku, sku);
  }
  
  const equipmentFilter = filter(data?.selectableEquipment ?? [], ['category', equipmentCategory]);
  return (
    <>
      <span className="label">Tillegg</span>
      {equipmentFilter?.length === 0 ? (
        <p> Ingen Tillegg tilgjengelig</p>
      ) : (
        <CarConfigureGroupStyled className="vehicleSelectableEquipment">
          {data?.selectableEquipment?.map?.((equipment) => {
            const isSelected = selectedEquipmentIds?.find((selected) => selected?.id === equipment?.id);
            const isDisableRule = isDisableRuleApply(equipment);
            const customPriceTag =
              getPrice(equipment) > 0
                ? `+ ${priceAdditionCheckToSelectable(equipment) || ''}`
                : priceAdditionCheckToSelectable(equipment);
            return equipmentCategory === equipment?.category ? (
              <Card
                key={equipment?.id}
                id={equipment?.id}
                title={equipment?.name}
                customPriceTag={customPriceTag}
                type={CardType.BosCard}
                selected={Boolean(isSelected)}
                isDisabled={disabled || isDisableRule}
                alertMessage={{
                  message: isDisabled(equipment?.sku) ? getSkuNote(equipment?.sku) : '',
                  typeOfAlert: MessagebarSeverityLevel.WARNING
                }}
                displayTag
                disableTag={!isDataAvailable(equipment)}
                tagValue={equipment?.sku}
                onTagClick={() => setCurrentData(equipment)}
                updateSelected={(id) => {
                  onSelectableInputChange(id, equipment);
                }}
              />
            ) : null;
          })}
          <VehicleSelectableDescription data={activeDesc} active={active} onClose={() => setActive(!active)} />
        </CarConfigureGroupStyled>
      )}
    </>
  );
};
